import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const STORAGE_KEY = 'trending-app-filters';

/**
 * Default filters for each view
 */
const DEFAULT_FILTERS = {
  dashboard: {
    region_code: 'GLOBAL',
    platform: 'all',
    media_type: 'all',
    time_window: '24h',
    sort_by: 'score',
    limit: 30
  },
  media: {
    region: 'GLOBAL',
    platform: 'youtube',
    media_type: 'video',
    limit: 30
  },
  tagDetail: {
    mediaTypeFilter: 'all',
    activeTab: 'overview'
  }
};

/**
 * Load filters from sessionStorage
 * @returns {Object} Stored filters or empty object
 */
const loadFiltersFromStorage = () => {
  if (typeof window === 'undefined') return {};
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (err) {
    console.warn('Failed to load filters from sessionStorage:', err);
    return {};
  }
};

/**
 * Save filters to sessionStorage
 * @param {Object} filters - Filters to save
 */
const saveFiltersToStorage = (filters) => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  } catch (err) {
    console.warn('Failed to save filters to sessionStorage:', err);
  }
};

const FilterContext = createContext(null);

/**
 * FilterProvider: Manages filter state per view with sessionStorage persistence.
 * 
 * Features:
 * - Per-view filter state (dashboard, media, tagDetail)
 * - Automatic sessionStorage sync
 * - Filters persist during browser session (survives navigation, cleared on tab close)
 * - Perfect for Cloud Run stateless deployment
 * 
 * Usage:
 *   const { getFilters, setFilter, resetFilters } = useFilters();
 *   const dashboardFilters = getFilters('dashboard');
 *   setFilter('dashboard', 'region_code', 'SG');
 */
export function FilterProvider({ children }) {
  // Initialize state from sessionStorage
  const [filters, setFiltersState] = useState(() => {
    const stored = loadFiltersFromStorage();
    // Merge stored filters with defaults to ensure all keys exist
    return {
      dashboard: { ...DEFAULT_FILTERS.dashboard, ...stored.dashboard },
      media: { ...DEFAULT_FILTERS.media, ...stored.media },
      tagDetail: { ...DEFAULT_FILTERS.tagDetail, ...stored.tagDetail }
    };
  });

  // Sync to sessionStorage whenever filters change
  useEffect(() => {
    saveFiltersToStorage(filters);
  }, [filters]);

  /**
   * Get filters for a specific view
   * @param {string} viewId - View identifier ('dashboard', 'media', 'tagDetail')
   * @returns {Object} Filters for the view
   */
  const getFilters = useCallback((viewId) => {
    return filters[viewId] || DEFAULT_FILTERS[viewId] || {};
  }, [filters]);

  // Memoize filter objects to prevent unnecessary re-renders
  // Use JSON.stringify to create stable keys for comparison
  const dashboardKey = JSON.stringify(filters.dashboard || DEFAULT_FILTERS.dashboard);
  const mediaKey = JSON.stringify(filters.media || DEFAULT_FILTERS.media);
  const tagDetailKey = JSON.stringify(filters.tagDetail || DEFAULT_FILTERS.tagDetail);
  
  const memoizedFilters = useMemo(() => ({
    dashboard: filters.dashboard || DEFAULT_FILTERS.dashboard,
    media: filters.media || DEFAULT_FILTERS.media,
    tagDetail: filters.tagDetail || DEFAULT_FILTERS.tagDetail
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [dashboardKey, mediaKey, tagDetailKey]); // Using JSON keys for stable comparison

  /**
   * Set a single filter value for a view
   * @param {string} viewId - View identifier
   * @param {string} key - Filter key
   * @param {any} value - Filter value
   */
  const setFilter = useCallback((viewId, key, value) => {
    setFiltersState(prev => ({
      ...prev,
      [viewId]: {
        ...(prev[viewId] || DEFAULT_FILTERS[viewId] || {}),
        [key]: value
      }
    }));
  }, []);

  /**
   * Set multiple filters at once for a view
   * @param {string} viewId - View identifier
   * @param {Object} newFilters - Object with filter key-value pairs
   */
  const setFilters = useCallback((viewId, newFilters) => {
    setFiltersState(prev => ({
      ...prev,
      [viewId]: {
        ...(prev[viewId] || DEFAULT_FILTERS[viewId] || {}),
        ...newFilters
      }
    }));
  }, []);

  /**
   * Reset filters for a view to defaults
   * @param {string} viewId - View identifier
   */
  const resetFilters = useCallback((viewId) => {
    setFiltersState(prev => ({
      ...prev,
      [viewId]: { ...DEFAULT_FILTERS[viewId] }
    }));
  }, []);

  /**
   * Reset all filters to defaults
   */
  const resetAllFilters = useCallback(() => {
    setFiltersState({
      dashboard: { ...DEFAULT_FILTERS.dashboard },
      media: { ...DEFAULT_FILTERS.media },
      tagDetail: { ...DEFAULT_FILTERS.tagDetail }
    });
  }, []);

  return (
    <FilterContext.Provider value={{ 
      getFilters, 
      setFilter, 
      setFilters, 
      resetFilters, 
      resetAllFilters,
      DEFAULT_FILTERS,
      memoizedFilters
    }}>
      {children}
    </FilterContext.Provider>
  );
}

/**
 * Hook to access filter context
 * @returns {Object} Filter context value
 */
export function useFilters() {
  const ctx = useContext(FilterContext);
  if (!ctx) throw new Error('useFilters must be used within FilterProvider');
  return ctx;
}

/**
 * Hook for dashboard filters (convenience wrapper)
 * @returns {Object} { filters, setFilter, resetFilters }
 */
export function useDashboardFilters() {
  const { memoizedFilters, setFilter, resetFilters } = useFilters();
  return useMemo(() => ({
    filters: memoizedFilters.dashboard,
    setFilter: (key, value) => setFilter('dashboard', key, value),
    resetFilters: () => resetFilters('dashboard')
  }), [memoizedFilters.dashboard, setFilter, resetFilters]);
}

/**
 * Hook for media view filters (convenience wrapper)
 * @returns {Object} { filters, setFilter, resetFilters }
 */
export function useMediaFilters() {
  const { memoizedFilters, setFilter, resetFilters } = useFilters();
  return useMemo(() => ({
    filters: memoizedFilters.media,
    setFilter: (key, value) => setFilter('media', key, value),
    resetFilters: () => resetFilters('media')
  }), [memoizedFilters.media, setFilter, resetFilters]);
}

/**
 * Hook for tag detail filters (convenience wrapper)
 * @returns {Object} { filters, setFilter, resetFilters }
 */
export function useTagDetailFilters() {
  const { memoizedFilters, setFilter, resetFilters } = useFilters();
  return useMemo(() => ({
    filters: memoizedFilters.tagDetail,
    setFilter: (key, value) => setFilter('tagDetail', key, value),
    resetFilters: () => resetFilters('tagDetail')
  }), [memoizedFilters.tagDetail, setFilter, resetFilters]);
}
